import React, { useState, useEffect } from 'react';
import {
  User, DollarSign, Save, Plus, Trash2, Edit2, CreditCard,
  Calendar, Percent, AlertCircle, CheckCircle2, FileText,
  Building, ChevronDown, ChevronUp, Search, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Alert, AlertDescription } from '../../ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { db } from '../../../lib/db';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import {
  PAY_FREQUENCIES,
  DEDUCTION_TYPES,
  getStatesForDropdown,
  formatUSDCurrency,
  annualToPerPeriod,
  hourlyToAnnual
} from '../../../lib/usPayrollCalculations';
import { RETIREMENT_LIMITS_2024, HEALTH_ACCOUNT_LIMITS_2024 } from '../../../lib/usPayrollData';
import { useConfirmDialog } from '../../shared/ConfirmDialog';

const US_STATES = getStatesForDropdown();

const FILING_STATUSES = [
  { value: 'single', label: 'Single or Married Filing Separately' },
  { value: 'married', label: 'Married Filing Jointly' },
  { value: 'headOfHousehold', label: 'Head of Household' }
];

const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' }
];

const DEPOSIT_TYPES = [
  { value: 'full', label: 'Full Amount' },
  { value: 'fixed', label: 'Fixed Amount' },
  { value: 'percentage', label: 'Percentage' }
];

export default function EmployeeCompensationSetup() {
  const { confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [payGroups, setPayGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('compensation');

  // Employee data states
  const [compensation, setCompensation] = useState(null);
  const [w4Data, setW4Data] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [taxOverrides, setTaxOverrides] = useState([]);

  // Modal states
  const [showBankModal, setShowBankModal] = useState(false);
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [editingDeduction, setEditingDeduction] = useState(null);

  // Form states
  const [newBank, setNewBank] = useState({
    bankName: '',
    accountNumber: '',
    routingNumber: '',
    accountType: 'checking',
    depositType: 'full',
    amount: '',
    percentage: 100,
    priority: 1
  });

  const [newDeduction, setNewDeduction] = useState({
    type: 'traditional401k',
    amount: '',
    isPercentage: false,
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      loadEmployeeData(selectedEmployee.id);
    }
  }, [selectedEmployee]);

  const loadEmployees = async () => {
    try {
      const [users, groups] = await Promise.all([
        db.users.where('role').notEqual('admin').toArray(),
        db.payGroups.toArray()
      ]);
      setEmployees(users.filter(u => u.status === 'active'));
      setPayGroups(groups.filter(g => g.status === 'active'));
    } catch (error) {
      console.error('Error loading employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeData = async (userId) => {
    try {
      // Get employee profile data for state defaults
      const employee = employees.find(e => e.id === userId);
      const profileWorkState = employee?.profile?.workState || '';
      const profileResidentState = employee?.profile?.residentState || profileWorkState;

      const [comp, w4, banks, deds, overrides, deductionCodes] = await Promise.all([
        db.employeeCompensation.where('userId').equals(userId).first(),
        db.employeeW4.where('userId').equals(userId).first(),
        db.employeeBankAccounts.where('userId').equals(userId).toArray(),
        db.employeeDeductions.where('userId').equals(userId).toArray(),
        db.employeeTaxOverrides.where('userId').equals(userId).toArray(),
        db.deductionCodes.toArray()
      ]);

      // Map deductionCodeId to DEDUCTION_TYPES key
      const codeToTypeMap = {
        '401K': 'traditional401k',
        '403B': 'traditional403b',
        '457': 'traditional457',
        'SIRA': 'simpleIRA',
        'HSA': 'hsa',
        'FSA': 'fsa',
        'DCFSA': 'dcfsa',
        'HLTH': 'healthInsurance',
        'HEALTH': 'healthInsurance',
        'DENT': 'dentalInsurance',
        'DENTAL': 'dentalInsurance',
        'VIS': 'visionInsurance',
        'VISION': 'visionInsurance',
        'LIFE': 'lifeInsurance',
        'COMM': 'commuter',
        'R401K': 'roth401k',
        'ROTH': 'roth401k',
        'RIRA': 'rothIRA',
        'LIFEP': 'lifeInsurancePost',
        'DIS': 'disabilityInsurance',
        'CSUP': 'childSupport',
        'LEVY': 'taxLevy',
        'STLN': 'studentLoan',
        'GARN': 'creditorGarnishment',
        'GARNISH': 'creditorGarnishment',
        'UNION': 'unionDues',
        'CHAR': 'charityDeduction',
        'LOAN': 'loanRepayment'
      };

      // Enrich deductions with type field from deductionCodes
      const enrichedDeductions = (deds || []).map(ded => {
        const deductionCode = deductionCodes.find(dc => dc.id === ded.deductionCodeId);
        const codeStr = deductionCode?.code || ded.code || '';
        const type = codeToTypeMap[codeStr] || ded.type || codeStr.toLowerCase();
        return {
          ...ded,
          code: codeStr,
          type: type
        };
      });

      setCompensation(comp || {
        id: uuidv4(),
        userId,
        payType: 'salary',
        payRate: '',
        payFrequency: 'biweekly',
        payGroupId: '',
        effectiveDate: new Date().toISOString().split('T')[0],
        status: 'active',
        workState: profileWorkState,
        residentState: profileResidentState
      });

      setW4Data(w4 || {
        id: uuidv4(),
        userId,
        filingStatus: employee?.profile?.taxWithholding?.federal?.filingStatus || 'single',
        multipleJobs: employee?.profile?.taxWithholding?.federal?.multipleJobs || false,
        dependentCredit: employee?.profile?.taxWithholding?.federal?.dependentCredit || 0,
        qualifyingChildren: employee?.profile?.taxWithholding?.federal?.childrenCount || 0,
        otherDependents: employee?.profile?.taxWithholding?.federal?.otherDependentsCount || 0,
        otherIncome: employee?.profile?.taxWithholding?.federal?.otherIncome || 0,
        deductions: employee?.profile?.taxWithholding?.federal?.deductions || 0,
        additionalWithholding: employee?.profile?.taxWithholding?.federal?.extraWithholding || 0,
        exempt: false,
        effectiveDate: new Date().toISOString().split('T')[0]
      });

      setBankAccounts(banks || []);
      setDeductions(enrichedDeductions);
      setTaxOverrides(overrides || []);
    } catch (error) {
      console.error('Error loading employee data:', error);
    }
  };

  const handleSaveCompensation = async () => {
    try {
      await db.employeeCompensation.put({
        ...compensation,
        updatedAt: Date.now()
      });
      toast.success('Compensation saved successfully');
    } catch (error) {
      toast.error('Failed to save compensation');
    }
  };

  const handleSaveW4 = async () => {
    try {
      await db.employeeW4.put({
        ...w4Data,
        updatedAt: Date.now()
      });
      toast.success('W-4 information saved successfully');
    } catch (error) {
      toast.error('Failed to save W-4 information');
    }
  };

  const handleAddBankAccount = async () => {
    try {
      const bank = {
        id: uuidv4(),
        userId: selectedEmployee.id,
        ...newBank,
        status: 'active',
        createdAt: Date.now()
      };
      await db.employeeBankAccounts.add(bank);
      setBankAccounts([...bankAccounts, bank]);
      setShowBankModal(false);
      resetBankForm();
      toast.success('Bank account added successfully');
    } catch (error) {
      toast.error('Failed to add bank account');
    }
  };

  const handleUpdateBankAccount = async () => {
    try {
      await db.employeeBankAccounts.put({
        ...editingBank,
        updatedAt: Date.now()
      });
      setBankAccounts(bankAccounts.map(b => b.id === editingBank.id ? editingBank : b));
      setEditingBank(null);
      toast.success('Bank account updated');
    } catch (error) {
      toast.error('Failed to update bank account');
    }
  };

  const handleDeleteBankAccount = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Bank Account',
      description: 'Are you sure you want to delete this bank account?',
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (!confirmed) return;
    try {
      await db.employeeBankAccounts.delete(id);
      setBankAccounts(bankAccounts.filter(b => b.id !== id));
      toast.success('Bank account deleted');
    } catch (error) {
      toast.error('Failed to delete bank account');
    }
  };

  const handleAddDeduction = async () => {
    try {
      const ded = {
        id: uuidv4(),
        userId: selectedEmployee.id,
        deductionCodeId: newDeduction.type,
        ...newDeduction,
        status: 'active',
        createdAt: Date.now()
      };
      await db.employeeDeductions.add(ded);
      setDeductions([...deductions, ded]);
      setShowDeductionModal(false);
      resetDeductionForm();
      toast.success('Deduction added successfully');
    } catch (error) {
      toast.error('Failed to add deduction');
    }
  };

  const handleDeleteDeduction = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Deduction',
      description: 'Are you sure you want to delete this deduction?',
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (!confirmed) return;
    try {
      await db.employeeDeductions.delete(id);
      setDeductions(deductions.filter(d => d.id !== id));
      toast.success('Deduction deleted');
    } catch (error) {
      toast.error('Failed to delete deduction');
    }
  };

  const resetBankForm = () => {
    setNewBank({
      bankName: '',
      accountNumber: '',
      routingNumber: '',
      accountType: 'checking',
      depositType: 'full',
      amount: '',
      percentage: 100,
      priority: bankAccounts.length + 1
    });
  };

  const resetDeductionForm = () => {
    setNewDeduction({
      type: 'traditional401k',
      amount: '',
      isPercentage: false,
      startDate: '',
      endDate: ''
    });
  };

  const formatRoutingNumber = (value) => {
    return value.replace(/\D/g, '').slice(0, 9);
  };

  const maskAccountNumber = (num) => {
    if (!num) return '';
    return '••••' + num.slice(-4);
  };

  const calculateAnnualSalary = () => {
    if (!compensation?.payRate) return 0;
    if (compensation.payType === 'salary') {
      return parseFloat(compensation.payRate) || 0;
    }
    return hourlyToAnnual(parseFloat(compensation.payRate) || 0, 40);
  };

  const calculatePerPeriodPay = () => {
    const annual = calculateAnnualSalary();
    return annualToPerPeriod(annual, compensation?.payFrequency || 'biweekly');
  };

  const filteredEmployees = employees.filter(e =>
    `${e.profile?.firstName} ${e.profile?.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Compensation Setup</h1>
          <p className="text-gray-500 mt-1">Configure pay rates, W-4, direct deposit, and deductions</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Employee List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Employees</CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[600px] overflow-y-auto">
              {filteredEmployees.map(employee => (
                <div
                  key={employee.id}
                  onClick={() => setSelectedEmployee(employee)}
                  className={`flex items-center gap-3 p-3 cursor-pointer border-b hover:bg-gray-50 transition-colors ${selectedEmployee?.id === employee.id ? 'bg-teal-50 border-l-4 border-l-teal-500' : ''
                    }`}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-teal-400 to-emerald-500 flex items-center justify-center text-white font-medium">
                    {employee.profile?.firstName?.[0]}{employee.profile?.lastName?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {employee.profile?.firstName} {employee.profile?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{employee.profile?.position}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Employee Details */}
        <div className="lg:col-span-3">
          {!selectedEmployee ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <User className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-500">Select an Employee</h3>
                <p className="text-sm text-gray-400">Choose an employee from the list to configure their compensation</p>
              </CardContent>
            </Card>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">
                    {selectedEmployee.profile?.firstName} {selectedEmployee.profile?.lastName}
                  </h2>
                  <p className="text-sm text-gray-500">{selectedEmployee.email}</p>
                </div>
                <TabsList>
                  <TabsTrigger value="compensation">Compensation</TabsTrigger>
                  <TabsTrigger value="w4">W-4</TabsTrigger>
                  <TabsTrigger value="banking">Direct Deposit</TabsTrigger>
                  <TabsTrigger value="deductions">Deductions</TabsTrigger>
                </TabsList>
              </div>

              {/* Compensation Tab */}
              <TabsContent value="compensation">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-teal-600" />
                      Pay Information
                    </CardTitle>
                    <CardDescription>Configure employee's salary or hourly rate</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Pay Type</Label>
                        <Select
                          value={compensation?.payType || 'salary'}
                          onValueChange={(v) => setCompensation({ ...compensation, payType: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="salary">Salary (Annual)</SelectItem>
                            <SelectItem value="hourly">Hourly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>{compensation?.payType === 'salary' ? 'Annual Salary' : 'Hourly Rate'}</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            type="number"
                            placeholder={compensation?.payType === 'salary' ? '75000' : '25.00'}
                            value={compensation?.payRate || ''}
                            onChange={(e) => setCompensation({ ...compensation, payRate: e.target.value })}
                            className="pl-9"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Pay Group</Label>
                        <Select
                          value={compensation?.payGroupId || ''}
                          onValueChange={(v) => {
                            const group = payGroups.find(g => g.id === v);
                            setCompensation({
                              ...compensation,
                              payGroupId: v,
                              payFrequency: group?.frequency || 'biweekly'
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select pay group" />
                          </SelectTrigger>
                          <SelectContent>
                            {payGroups.map((group) => (
                              <SelectItem key={group.id} value={group.id}>
                                {group.name} ({group.frequency})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {payGroups.length === 0 && (
                          <p className="text-xs text-amber-600">No pay groups available. Create one in Pay Groups first.</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Effective Date</Label>
                        <Input
                          type="date"
                          value={compensation?.effectiveDate || ''}
                          onChange={(e) => setCompensation({ ...compensation, effectiveDate: e.target.value })}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Work State (Tax Withholding)</Label>
                        <Select
                          value={compensation?.workState || selectedEmployee?.profile?.workState || ''}
                          onValueChange={(v) => setCompensation({ ...compensation, workState: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select work state" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map(state => (
                              <SelectItem key={state.code} value={state.code}>
                                {state.name} {!state.hasStateTax && '(No state tax)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedEmployee?.profile?.workState && !compensation?.workState && (
                          <p className="text-xs text-gray-500">Pre-filled from employee profile</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Resident State</Label>
                        <Select
                          value={compensation?.residentState || selectedEmployee?.profile?.residentState || ''}
                          onValueChange={(v) => setCompensation({ ...compensation, residentState: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select resident state" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map(state => (
                              <SelectItem key={state.code} value={state.code}>
                                {state.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedEmployee?.profile?.residentState && !compensation?.residentState && (
                          <p className="text-xs text-gray-500">Pre-filled from employee profile</p>
                        )}
                      </div>
                    </div>

                    {/* Pay Summary */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium mb-3">Pay Summary</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Annual</p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatUSDCurrency(calculateAnnualSalary())}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Per Period</p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatUSDCurrency(calculatePerPeriodPay())}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Pay Periods/Year</p>
                          <p className="text-lg font-bold text-gray-900">
                            {PAY_FREQUENCIES[compensation?.payFrequency]?.periodsPerYear || 26}
                          </p>
                        </div>
                        {compensation?.payType === 'hourly' && (
                          <div>
                            <p className="text-xs text-gray-500">Weekly (40 hrs)</p>
                            <p className="text-lg font-bold text-gray-900">
                              {formatUSDCurrency((parseFloat(compensation?.payRate) || 0) * 40)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button onClick={handleSaveCompensation}>
                        <Save className="w-4 h-4 mr-2" /> Save Compensation
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* W-4 Tab */}
              <TabsContent value="w4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      W-4 Tax Withholding
                    </CardTitle>
                    <CardDescription>Federal income tax withholding elections</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Alert className="bg-blue-50 border-blue-200">
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        These settings reflect the employee's 2020 or later Form W-4 elections. For older W-4 forms, contact your payroll administrator.
                      </AlertDescription>
                    </Alert>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Filing Status (Step 1c)</Label>
                        <Select
                          value={w4Data?.filingStatus || 'single'}
                          onValueChange={(v) => setW4Data({ ...w4Data, filingStatus: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FILING_STATUSES.map(status => (
                              <SelectItem key={status.value} value={status.value}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Effective Date</Label>
                        <Input
                          type="date"
                          value={w4Data?.effectiveDate || ''}
                          onChange={(e) => setW4Data({ ...w4Data, effectiveDate: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg">
                      <Switch
                        checked={w4Data?.multipleJobs || false}
                        onCheckedChange={(v) => setW4Data({ ...w4Data, multipleJobs: v })}
                      />
                      <div>
                        <Label>Multiple Jobs or Spouse Works (Step 2c)</Label>
                        <p className="text-xs text-gray-500">Check if you hold more than one job or are married filing jointly and your spouse also works</p>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <h4 className="font-medium">Step 3: Claim Dependents</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Qualifying Children Under 17</Label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={w4Data?.qualifyingChildren || ''}
                            onChange={(e) => {
                              const children = parseInt(e.target.value) || 0;
                              const otherDeps = w4Data?.otherDependents || 0;
                              setW4Data({
                                ...w4Data,
                                qualifyingChildren: children,
                                dependentCredit: (children * 2000) + (otherDeps * 500)
                              });
                            }}
                          />
                          <p className="text-xs text-gray-500">× $2,000 each</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Other Dependents</Label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={w4Data?.otherDependents || ''}
                            onChange={(e) => {
                              const otherDeps = parseInt(e.target.value) || 0;
                              const children = w4Data?.qualifyingChildren || 0;
                              setW4Data({
                                ...w4Data,
                                otherDependents: otherDeps,
                                dependentCredit: (children * 2000) + (otherDeps * 500)
                              });
                            }}
                          />
                          <p className="text-xs text-gray-500">× $500 each</p>
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Total Dependent Credit:</span>
                          <span className="text-lg font-bold">${(w4Data?.dependentCredit || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <h4 className="font-medium">Step 4: Other Adjustments (Optional)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>(a) Other Income</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              type="number"
                              placeholder="0"
                              value={w4Data?.otherIncome || ''}
                              onChange={(e) => setW4Data({ ...w4Data, otherIncome: parseFloat(e.target.value) || 0 })}
                              className="pl-9"
                            />
                          </div>
                          <p className="text-xs text-gray-500">Interest, dividends, retirement income</p>
                        </div>

                        <div className="space-y-2">
                          <Label>(b) Deductions</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              type="number"
                              placeholder="0"
                              value={w4Data?.deductions || ''}
                              onChange={(e) => setW4Data({ ...w4Data, deductions: parseFloat(e.target.value) || 0 })}
                              className="pl-9"
                            />
                          </div>
                          <p className="text-xs text-gray-500">If itemizing exceeds standard deduction</p>
                        </div>

                        <div className="space-y-2">
                          <Label>(c) Extra Withholding</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              type="number"
                              placeholder="0"
                              value={w4Data?.additionalWithholding || ''}
                              onChange={(e) => setW4Data({ ...w4Data, additionalWithholding: parseFloat(e.target.value) || 0 })}
                              className="pl-9"
                            />
                          </div>
                          <p className="text-xs text-gray-500">Additional per pay period</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <Switch
                        checked={w4Data?.exempt || false}
                        onCheckedChange={(v) => setW4Data({ ...w4Data, exempt: v })}
                      />
                      <div>
                        <Label className="text-amber-800">Claim Exemption from Withholding</Label>
                        <p className="text-xs text-amber-700">Only if employee had no tax liability last year and expects none this year</p>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button onClick={handleSaveW4}>
                        <Save className="w-4 h-4 mr-2" /> Save W-4 Information
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Banking Tab */}
              <TabsContent value="banking">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-green-600" />
                        Direct Deposit Accounts
                      </CardTitle>
                      <CardDescription>Configure payment split across multiple accounts</CardDescription>
                    </div>
                    <Button onClick={() => { resetBankForm(); setShowBankModal(true); }}>
                      <Plus className="w-4 h-4 mr-2" /> Add Account
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {bankAccounts.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No bank accounts configured</p>
                        <p className="text-sm text-gray-400">Add a bank account for direct deposit</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Priority</TableHead>
                            <TableHead>Bank Name</TableHead>
                            <TableHead>Account</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Deposit</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bankAccounts.sort((a, b) => a.priority - b.priority).map(bank => (
                            <TableRow key={bank.id}>
                              <TableCell>{bank.priority}</TableCell>
                              <TableCell className="font-medium">{bank.bankName}</TableCell>
                              <TableCell>{maskAccountNumber(bank.accountNumber)}</TableCell>
                              <TableCell className="capitalize">{bank.accountType}</TableCell>
                              <TableCell>
                                {bank.depositType === 'full' && 'Remainder'}
                                {bank.depositType === 'fixed' && formatUSDCurrency(bank.amount)}
                                {bank.depositType === 'percentage' && `${bank.percentage}%`}
                              </TableCell>
                              <TableCell>
                                <Badge className={bank.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                                  {bank.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button variant="ghost" size="sm" onClick={() => setEditingBank(bank)}>
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteBankAccount(bank.id)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    <Alert className="mt-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Accounts are processed in priority order. The last account with "Full Amount" or "Remainder" receives any remaining funds.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Deductions Tab */}
              <TabsContent value="deductions">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Percent className="w-5 h-5 text-purple-600" />
                        Payroll Deductions
                      </CardTitle>
                      <CardDescription>Retirement, health, and voluntary deductions</CardDescription>
                    </div>
                    <Button onClick={() => { resetDeductionForm(); setShowDeductionModal(true); }}>
                      <Plus className="w-4 h-4 mr-2" /> Add Deduction
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {deductions.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Percent className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No deductions configured</p>
                        <p className="text-sm text-gray-400">Add 401(k), health insurance, or other deductions</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Pre-Tax Deductions */}
                        <div>
                          <h4 className="font-medium text-sm text-gray-500 mb-2">PRE-TAX DEDUCTIONS</h4>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Type</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Annual Limit</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {deductions.filter(d => DEDUCTION_TYPES[d.type]?.preTax).map(ded => {
                                const dedType = DEDUCTION_TYPES[ded.type];
                                return (
                                  <TableRow key={ded.id}>
                                    <TableCell className="font-medium">{dedType?.name || ded.type}</TableCell>
                                    <TableCell>
                                      {ded.isPercentage ? `${ded.amount}%` : formatUSDCurrency(ded.amount)}
                                    </TableCell>
                                    <TableCell>
                                      {dedType?.limit ? formatUSDCurrency(dedType.limit) : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button variant="ghost" size="sm" onClick={() => handleDeleteDeduction(ded.id)}>
                                        <Trash2 className="w-4 h-4 text-red-500" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Post-Tax Deductions */}
                        {deductions.filter(d => !DEDUCTION_TYPES[d.type]?.preTax).length > 0 && (
                          <div>
                            <h4 className="font-medium text-sm text-gray-500 mb-2">POST-TAX DEDUCTIONS</h4>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Type</TableHead>
                                  <TableHead>Amount</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {deductions.filter(d => !DEDUCTION_TYPES[d.type]?.preTax).map(ded => {
                                  const dedType = DEDUCTION_TYPES[ded.type];
                                  return (
                                    <TableRow key={ded.id}>
                                      <TableCell className="font-medium">{dedType?.name || ded.type}</TableCell>
                                      <TableCell>
                                        {ded.isPercentage ? `${ded.amount}%` : formatUSDCurrency(ded.amount)}
                                      </TableCell>
                                      <TableCell>
                                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <Button variant="ghost" size="sm" onClick={() => handleDeleteDeduction(ded.id)}>
                                          <Trash2 className="w-4 h-4 text-red-500" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Limits Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h5 className="font-medium text-blue-900">401(k) Limit</h5>
                        <p className="text-2xl font-bold text-blue-600">
                          {formatUSDCurrency(RETIREMENT_LIMITS_2024['401k'].employeeElective)}
                        </p>
                        <p className="text-xs text-blue-700">+$7,500 catch-up if 50+</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <h5 className="font-medium text-green-900">HSA Limit (Self)</h5>
                        <p className="text-2xl font-bold text-green-600">
                          {formatUSDCurrency(HEALTH_ACCOUNT_LIMITS_2024.hsa.selfOnly)}
                        </p>
                        <p className="text-xs text-green-700">Family: $8,300</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <h5 className="font-medium text-purple-900">FSA Limit</h5>
                        <p className="text-2xl font-bold text-purple-600">
                          {formatUSDCurrency(HEALTH_ACCOUNT_LIMITS_2024.fsa.healthCare)}
                        </p>
                        <p className="text-xs text-purple-700">Health care FSA</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>

      {/* Add Bank Account Modal */}
      <Dialog open={showBankModal || !!editingBank} onOpenChange={(open) => {
        if (!open) {
          setShowBankModal(false);
          setEditingBank(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBank ? 'Edit Bank Account' : 'Add Bank Account'}</DialogTitle>
            <DialogDescription>Enter bank account details for direct deposit</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Bank Name</Label>
              <Input
                placeholder="Bank of America"
                value={editingBank?.bankName || newBank.bankName}
                onChange={(e) => editingBank
                  ? setEditingBank({ ...editingBank, bankName: e.target.value })
                  : setNewBank({ ...newBank, bankName: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Routing Number</Label>
                <Input
                  placeholder="9 digits"
                  value={editingBank?.routingNumber || newBank.routingNumber}
                  onChange={(e) => editingBank
                    ? setEditingBank({ ...editingBank, routingNumber: formatRoutingNumber(e.target.value) })
                    : setNewBank({ ...newBank, routingNumber: formatRoutingNumber(e.target.value) })
                  }
                  maxLength={9}
                />
              </div>
              <div className="space-y-2">
                <Label>Account Number</Label>
                <Input
                  placeholder="Account number"
                  value={editingBank?.accountNumber || newBank.accountNumber}
                  onChange={(e) => editingBank
                    ? setEditingBank({ ...editingBank, accountNumber: e.target.value })
                    : setNewBank({ ...newBank, accountNumber: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Type</Label>
                <Select
                  value={editingBank?.accountType || newBank.accountType}
                  onValueChange={(v) => editingBank
                    ? setEditingBank({ ...editingBank, accountType: v })
                    : setNewBank({ ...newBank, accountType: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Deposit Type</Label>
                <Select
                  value={editingBank?.depositType || newBank.depositType}
                  onValueChange={(v) => editingBank
                    ? setEditingBank({ ...editingBank, depositType: v })
                    : setNewBank({ ...newBank, depositType: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPOSIT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(editingBank?.depositType || newBank.depositType) === 'fixed' && (
              <div className="space-y-2">
                <Label>Fixed Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="500"
                    value={editingBank?.amount || newBank.amount}
                    onChange={(e) => editingBank
                      ? setEditingBank({ ...editingBank, amount: e.target.value })
                      : setNewBank({ ...newBank, amount: e.target.value })
                    }
                    className="pl-9"
                  />
                </div>
              </div>
            )}

            {(editingBank?.depositType || newBank.depositType) === 'percentage' && (
              <div className="space-y-2">
                <Label>Percentage</Label>
                <div className="relative">
                  <Input
                    type="number"
                    placeholder="50"
                    value={editingBank?.percentage || newBank.percentage}
                    onChange={(e) => editingBank
                      ? setEditingBank({ ...editingBank, percentage: e.target.value })
                      : setNewBank({ ...newBank, percentage: e.target.value })
                    }
                  />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Priority</Label>
              <Input
                type="number"
                min="1"
                value={editingBank?.priority || newBank.priority}
                onChange={(e) => editingBank
                  ? setEditingBank({ ...editingBank, priority: parseInt(e.target.value) })
                  : setNewBank({ ...newBank, priority: parseInt(e.target.value) })
                }
              />
              <p className="text-xs text-gray-500">Lower numbers are processed first</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBankModal(false); setEditingBank(null); }}>
              Cancel
            </Button>
            <Button onClick={editingBank ? handleUpdateBankAccount : handleAddBankAccount}>
              {editingBank ? 'Update' : 'Add'} Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Deduction Modal */}
      <Dialog open={showDeductionModal} onOpenChange={setShowDeductionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Deduction</DialogTitle>
            <DialogDescription>Configure a new payroll deduction</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Deduction Type</Label>
              <Select
                value={newDeduction.type}
                onValueChange={(v) => setNewDeduction({ ...newDeduction, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="header-pretax" disabled className="font-bold text-gray-500">
                    — Pre-Tax Deductions —
                  </SelectItem>
                  {Object.entries(DEDUCTION_TYPES).filter(([_, d]) => d.preTax).map(([key, ded]) => (
                    <SelectItem key={key} value={key}>{ded.name}</SelectItem>
                  ))}
                  <SelectItem value="header-posttax" disabled className="font-bold text-gray-500">
                    — Post-Tax Deductions —
                  </SelectItem>
                  {Object.entries(DEDUCTION_TYPES).filter(([_, d]) => !d.preTax).map(([key, ded]) => (
                    <SelectItem key={key} value={key}>{ded.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={newDeduction.isPercentage}
                onCheckedChange={(v) => setNewDeduction({ ...newDeduction, isPercentage: v })}
              />
              <Label>Calculate as percentage of gross pay</Label>
            </div>

            <div className="space-y-2">
              <Label>{newDeduction.isPercentage ? 'Percentage' : 'Amount per Pay Period'}</Label>
              <div className="relative">
                {!newDeduction.isPercentage && (
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                )}
                <Input
                  type="number"
                  placeholder={newDeduction.isPercentage ? '6' : '200'}
                  value={newDeduction.amount}
                  onChange={(e) => setNewDeduction({ ...newDeduction, amount: e.target.value })}
                  className={!newDeduction.isPercentage ? 'pl-9' : ''}
                />
                {newDeduction.isPercentage && (
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date (Optional)</Label>
                <Input
                  type="date"
                  value={newDeduction.startDate}
                  onChange={(e) => setNewDeduction({ ...newDeduction, startDate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date (Optional)</Label>
                <Input
                  type="date"
                  value={newDeduction.endDate}
                  onChange={(e) => setNewDeduction({ ...newDeduction, endDate: e.target.value })}
                />
              </div>
            </div>

            {DEDUCTION_TYPES[newDeduction.type]?.limit && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Annual limit for {DEDUCTION_TYPES[newDeduction.type].name}: {formatUSDCurrency(DEDUCTION_TYPES[newDeduction.type].limit)}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeductionModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddDeduction}>
              Add Deduction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
